/**
 * Accounts Hook
 *
 * Manages connected accounts (Google, Microsoft, Slack) for the current user.
 * Supports both preview mode (dummy data) and production mode (real backend data).
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { useCurrentUser } from './use-current-user';
import { isLovablePreview } from '@/lib/utils/environment';
import { useWorkspaceId } from './use-workspace-id';
import {
  useGetUserIntegrationsQuery,
  useDisconnectUserIntegrationMutation,
  useGetCrmOauthUrlLazyQuery,
  UserIntegrationPlatform,
  UserIntegrationStatus,
} from '@/lib/graphql/types-and-hooks';

export interface ConnectedAccount {
  id: string;
  name: string;
  platform: UserIntegrationPlatform;
  logo: string;
  email: string;
  connected: boolean;
  needsReconnect: boolean;
  addedTs?: string;
}

// Not every user-integration platform is a connected "account" shown here.
// Granola, for example, is surfaced in Call Recorder's recorder dropdown, so it has
// no config — hence Partial. Callers must handle a missing entry.
const ACCOUNT_CONFIGS: Partial<Record<UserIntegrationPlatform, { name: string; logo: string }>> = {
  [UserIntegrationPlatform.GoogleCalendar]: {
    name: 'Google Workspace',
    logo: '/logos/google.svg',
  },
  [UserIntegrationPlatform.Microsoft]: {
    name: 'Microsoft 365',
    logo: '/logos/microsoft.svg',
  },
  [UserIntegrationPlatform.Slack]: {
    name: 'Slack',
    logo: '/logos/slack.svg',
  },
  [UserIntegrationPlatform.Zoom]: {
    name: 'Zoom',
    logo: '/logos/zoom.svg',
  },
};

export function useAccounts(providerOverride?: 'google' | 'microsoft') {
  const { currentUser } = useCurrentUser();
  const preview = isLovablePreview();
  const workspaceId = useWorkspaceId();
  const userEmail = currentUser?.email || '';

  // Fetch user integrations from backend
  const { data, loading, refetch } = useGetUserIntegrationsQuery({
    skip: preview || !workspaceId,
    variables: {
      workspaceId: workspaceId ? parseInt(workspaceId) : 0,
    },
    fetchPolicy: 'cache-and-network',
  });

  const [disconnectMutation] = useDisconnectUserIntegrationMutation();
  const [getCrmOauthUrl, { data: oauthData }] = useGetCrmOauthUrlLazyQuery({});

  // When OAuth URL is received, redirect to it in same window
  useEffect(() => {
    const url = oauthData?.crmOauthUrl;
    if (url) {
      window.location.href = url;
    }
  }, [oauthData]);

  // Determine the user's sign-in provider: explicit override > user profile > default google
  const signInProvider = providerOverride || (currentUser?.oauthPlatform === 'microsoft' ? 'microsoft' : 'google');
  const calendarPlatform = signInProvider === 'microsoft' ? UserIntegrationPlatform.Microsoft : UserIntegrationPlatform.GoogleCalendar;

  // Preview mode dummy data — only show the calendar provider the user signed in with
  const previewAccounts: ConnectedAccount[] = useMemo(() => {
    if (!preview) return [];

    return [
      {
        id: calendarPlatform,
        name: ACCOUNT_CONFIGS[calendarPlatform]!.name,
        platform: calendarPlatform,
        logo: ACCOUNT_CONFIGS[calendarPlatform]!.logo,
        email: userEmail,
        connected: true,
        needsReconnect: false,
      },
      {
        id: UserIntegrationPlatform.Slack,
        name: ACCOUNT_CONFIGS[UserIntegrationPlatform.Slack]!.name,
        platform: UserIntegrationPlatform.Slack,
        logo: ACCOUNT_CONFIGS[UserIntegrationPlatform.Slack]!.logo,
        email: '',
        connected: false,
        needsReconnect: false,
      },
      {
        id: UserIntegrationPlatform.Zoom,
        name: ACCOUNT_CONFIGS[UserIntegrationPlatform.Zoom]!.name,
        platform: UserIntegrationPlatform.Zoom,
        logo: ACCOUNT_CONFIGS[UserIntegrationPlatform.Zoom]!.logo,
        email: '',
        connected: false,
        needsReconnect: false,
      },
    ];
  }, [preview, userEmail, calendarPlatform]);

  // Production mode: map backend data to account list
  // Filter out the opposite email provider in production too
  const excludedPlatform = signInProvider === 'microsoft' ? UserIntegrationPlatform.GoogleCalendar : UserIntegrationPlatform.Microsoft;

  const productionAccounts: ConnectedAccount[] = useMemo(() => {
    if (preview) return [];

    const integrations = data?.userIntegrations || [];
    // Only platforms that are shown as connected "accounts" here. Excludes the
    // opposite calendar provider and any user-integration platform without an
    // account config (e.g. Granola, which is a recorder shown under Recorder
    // Source, not a connected account) — avoids a crash on config.name.
    const platforms = Object.values(UserIntegrationPlatform).filter(
      p => p !== excludedPlatform && ACCOUNT_CONFIGS[p],
    );

    return platforms.flatMap(platform => {
      const config = ACCOUNT_CONFIGS[platform];
      // `platforms` is already filtered to entries with a config; this guard is
      // just to satisfy the type checker (and drop the entry if it ever isn't).
      if (!config) return [];
      const integration = integrations.find(i => i.platform === platform);

      return [{
        id: platform,
        name: config.name,
        platform,
        logo: config.logo,
        email: integration?.status === UserIntegrationStatus.Active ? userEmail : '',
        connected: integration?.status === UserIntegrationStatus.Active,
        needsReconnect: integration?.status === UserIntegrationStatus.Inactive,
        addedTs: integration?.addedTs || undefined,
      }];
    });
  }, [preview, data, userEmail, excludedPlatform]);

  const accounts = preview ? previewAccounts : productionAccounts;

  // Connect account (opens OAuth flow)
  const connectAccount = useCallback(async (platform: UserIntegrationPlatform) => {
    if (preview) {
      // Preview mode: just simulate connection
      return { success: true };
    }

    if (!workspaceId) {
      return { success: false, error: 'No workspace found' };
    }

    try {
      // Get OAuth URL from backend and redirect to same window
      // Send full URL so backend redirects back to the correct origin
      const currentUrl = new URL(window.location.href);
      const redirectTo = currentUrl.origin + currentUrl.pathname + currentUrl.search;
      await getCrmOauthUrl({
        variables: {
          workspaceId: parseInt(workspaceId),
          platform: platform, // Pass enum directly - backend handles it
          redirectTo,
        },
      });

      // The useEffect will handle the redirect when oauthData is set
      return { success: true };
    } catch (error) {
      console.error('Error connecting account:', error);
      return { success: false, error: String(error) };
    }
  }, [preview, workspaceId, getCrmOauthUrl]);

  // Disconnect account
  const disconnectAccount = useCallback(async (platform: UserIntegrationPlatform) => {
    if (preview) {
      // Preview mode: just simulate disconnection
      return { success: true };
    }

    if (!workspaceId) {
      return { success: false, error: 'No workspace found' };
    }

    try {
      const result = await disconnectMutation({
        variables: {
          workspaceId: parseInt(workspaceId),
          platform: platform, // Pass enum directly (not lowercased)
        },
        refetchQueries: ['getSlackUserIntegration'],
      });

      if (result.data?.disconnectUserIntegration?.ok) {
        // Refetch with workspaceId parameter like reference implementation
        await refetch({
          workspaceId: parseInt(workspaceId),
        });
        return { success: true };
      }

      return { success: false, error: 'Failed to disconnect account' };
    } catch (error) {
      console.error('Error disconnecting account:', error);
      return { success: false, error: String(error) };
    }
  }, [preview, workspaceId, disconnectMutation, refetch]);

  // Reconnect account (re-authenticate)
  const reconnectAccount = useCallback(async (platform: UserIntegrationPlatform) => {
    // Reconnecting uses the same OAuth flow as connecting
    return connectAccount(platform);
  }, [connectAccount]);

  return {
    accounts,
    loading: !preview && loading,
    connectAccount,
    disconnectAccount,
    reconnectAccount,
    refetch,
  };
}
